import { useTranslation } from 'react-i18next';

import { env } from '@/config/env';
import { isTerminalStatus } from '@/constants/deposit';
import { buildMerchantReturnUrl } from '@/helpers/returnUrl';
import { currentLocale } from '@/i18n/i18n';
import { useSessionState } from '@/state';

/**
 * Shared copy + deposit fields for funnel screens. Pages stay dumb renderers.
 */
export function useFunnelScreen() {
  const state = useSessionState();
  const { t } = useTranslation('cashier');
  const { t: tCommon } = useTranslation('common');
  const { t: tOutcome } = useTranslation('outcome');
  const locale = currentLocale();
  const deposit = state.deposit;
  const branding = state.branding;
  const countdownExpiresAt =
    deposit === null || isTerminalStatus(deposit.status)
      ? null
      : deposit.status === 'pending_review'
        ? deposit.verificationExpiresAt
        : deposit.expiresAt;

  const copy = {
    copyLabel: tCommon('actions.copy'),
    copiedLabel: tCommon('actions.copied'),
    manualCopyLabel: tCommon('actions.manualCopy'),
    emailLabel: tCommon('footer.supportEmail'),
    phoneLabel: tCommon('footer.supportPhone'),
    fallbackLabel: tCommon('footer.support'),
    supportHeading: tOutcome('support.title'),
    goHomeLabel: tCommon('actions.goHome'),
  };

  const summary =
    deposit === null
      ? null
      : {
          amount: deposit.amount,
          currency: deposit.currency,
          decimalPlaces: deposit.decimalPlaces,
          locale,
          amountLabel: t('amount.label'),
          receivedAmount: deposit.receivedAmount,
          requestedLabel: t('amount.requestedLabel'),
          receivedLabel: t('amount.receivedLabel'),
          publicId: deposit.publicId,
          referenceLabel: t('reference.label'),
          referenceHelp: t('reference.help'),
          countdownLabel: t('countdown.label'),
          expiresAt: countdownExpiresAt,
          ...copy,
        };

  const support = {
    email: branding?.supportEmail ?? null,
    phone: branding?.supportPhone ?? null,
    fallbackUrl: env.supportUrl,
    heading: copy.supportHeading,
    emailLabel: copy.emailLabel,
    phoneLabel: copy.phoneLabel,
    fallbackLabel: copy.fallbackLabel,
  };

  const returnUrl =
    deposit === null
      ? null
      : buildMerchantReturnUrl(deposit.returnUrl, deposit.publicId, deposit.status);

  return {
    state,
    deposit,
    branding,
    summary,
    support,
    returnUrl,
    goHomeLabel: copy.goHomeLabel,
    locale,
    t,
    tCommon,
  };
}
